// Enterprise Warranty Search and Filter Component
import React from 'react';
import type { WarrantyStats, WarrantyFilters } from '../../types/warranty';
import '../../styles/components/WarrantySearchFilters.css';

// Cache busting utility - only for development
const getCacheBustedIconUrl = (iconName: string) => {
  // Only add timestamp in development
  if (import.meta.env.DEV) {
    const timestamp = Date.now();
    return `/icons/${iconName}.svg?v=${timestamp}`;
  }
  // In production, use normal path for proper caching
  return `/icons/${iconName}.svg`;
};

// Simple icon component for using SVG files with cache busting
const Icon = ({ name, className = "" }: { name: string; className?: string }) => {
  return (
    <img 
      src={getCacheBustedIconUrl(name)} 
      alt="" 
      className={`icon ${className}`}
      onError={(e) => {
        console.error(`Failed to load icon: ${name}.svg`);
        // Fallback to a different approach if needed
        e.currentTarget.style.display = 'none';
      }}
    />
  );
};

interface WarrantySearchFiltersProps {
  filters: WarrantyFilters;
  stats: WarrantyStats;
  onFiltersChange: (filters: Partial<WarrantyFilters>) => void;
  onClearFilters: () => void;
  categories: string[];
  providers: string[];
  showAdvancedFilters: boolean;
  onToggleAdvancedFilters: () => void;
}

export const WarrantySearchFilters: React.FC<WarrantySearchFiltersProps> = ({
  filters,
  stats,
  onFiltersChange,
  onClearFilters,
  categories,
  providers,
  showAdvancedFilters,
  onToggleAdvancedFilters
}) => {
  return (
    <section className="compact-filter-section">
      <div className="search-filter-row">
        <input
          type="text"
          placeholder="Search warranties..."
          value={filters.searchTerm}
          onChange={(e) => onFiltersChange({ searchTerm: e.target.value })}
          className="compact-search-input expanded"
        />
        
        {/* Clickable Stats */}
        <div className="search-stats">
          <button 
            className={`mini-stat clickable ${filters.statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => onFiltersChange({ statusFilter: 'all' })}
          >
            Total: {stats.total}
          </button>
          <button 
            className={`mini-stat clickable ${filters.statusFilter === 'active' ? 'active' : ''}`}
            onClick={() => onFiltersChange({ statusFilter: 'active' })}
          >
            Active: {stats.active}
          </button>
          <button 
            className={`mini-stat clickable ${filters.statusFilter === 'expiring' ? 'active' : ''}`}
            onClick={() => onFiltersChange({ statusFilter: 'expiring' })}
          >
            Expiring: {stats.expiring}
          </button>
          <button 
            className={`mini-stat clickable ${filters.statusFilter === 'expired' ? 'active' : ''}`}
            onClick={() => onFiltersChange({ statusFilter: 'expired' })}
          >
            Expired: {stats.expired}
          </button>
        </div>
        
        <button 
          className="filter-toggle-btn"
          onClick={onToggleAdvancedFilters}
        >
          <Icon name="manage" /> More Filters {showAdvancedFilters ? '▼' : '▶'}
        </button>
      </div>
      
      {showAdvancedFilters && (
        <div className="inline-filters">
          <select 
            value={filters.categoryFilter} 
            onChange={(e) => onFiltersChange({ categoryFilter: e.target.value as WarrantyFilters['categoryFilter'] })} 
            className="inline-filter-select"
          >
            <option value="all">All Categories</option>
            {categories.filter(Boolean).map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
          
          <select 
            value={filters.providerFilter} 
            onChange={(e) => onFiltersChange({ providerFilter: e.target.value })} 
            className="inline-filter-select"
          >
            <option value="all">All Providers</option>
            {providers.filter(Boolean).map(provider => (
              <option key={provider} value={provider}>{provider}</option>
            ))}
          </select>
          
          <div className="filter-actions">
            <button 
              className="btn btn-secondary btn-sm"
              onClick={onClearFilters}
              title="Clear all filters"
            >
              <Icon name="bin" /> Clear All
            </button>
            <span className="filter-count">
              {Object.values(filters).filter(value => value !== 'all' && value !== '').length} active
            </span>
          </div>
        </div>
      )}
    </section>
  );
};
